import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Pencil, Trash2, Calendar, TrendingUp } from 'lucide-react';
import type { Coupon } from '../pages/Coupan/index';

interface CouponListProps {
  coupons: any;
  onDelete: (id: string) => void;
  onEdit: (coupon: Coupon) => void;
}

export function CouponList({ coupons, onDelete, onEdit }: CouponListProps) {
  const getStatusBadge = (status: Coupon['status']) => {
    switch (status) {
      case 'active':
        return (
          <Badge className="bg-green-600 hover:bg-green-700">Active</Badge>
        );
      case 'inactive':
        return (
          <Badge variant="secondary">Inactive</Badge>
        );
      case 'expired':
        return (
          <Badge variant="destructive">Expired</Badge>
        );
    }
  };

  const getActivationBadge = (status: Coupon['activationStatus']) => {
    switch (status) {
      case 'activating':
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            Activating
          </Badge>
        );
      case 'activated':
        return (
          <Badge className="bg-green-600 hover:bg-green-700">Activated</Badge>
        );
      case 'pending':
        return (
          <Badge variant="outline">Pending</Badge>
        );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>All Coupons</CardTitle>
        <p className="text-sm text-gray-500">
          Manage and monitor all your scratch coupons
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Coupon</TableHead>
                <TableHead>Offer For</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Activation</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {coupons.map((coupon:any) => (
                <TableRow key={coupon.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {coupon.imageUrl && (
                        <img
                          src={coupon.imageUrl}
                          alt={coupon.title}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span>{coupon.title}</span>
                          {coupon.isNew && (
                            <Badge className="bg-green-600 hover:bg-green-700 text-xs">
                              New
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </TableCell>
               
                  <TableCell>{coupon.forUsers}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="size-3 text-gray-400" />
                      <span className="text-sm">
                        {new Date(coupon.expiryDate).toLocaleDateString()}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({coupon.daysLeft}d left)
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(coupon.status)}</TableCell>
                  <TableCell>{getActivationBadge(coupon.activationStatus)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <TrendingUp className="size-3 text-gray-400" />
                      <span className="text-sm">
                        {coupon.usageCount}/{coupon.maxUsage}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(coupon)}
                      >
                        <Pencil className="size-4" />
                        <span className="sr-only">Edit</span>
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <Trash2 className="size-4 text-red-600" />
                            <span className="sr-only">Delete</span>
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Coupon</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{coupon.title}"?
                              This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => onDelete(coupon.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}